import React, { useMemo } from "react";
import { Form, FormInstance, RefSelectProps, Select, SelectProps, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import {
  SubjectCatalogItem,
  TrainingEducationLevel,
} from "../../../../services/formacion/formacion.types";
import {
  ALL_SCHOOLS,
  ALL_SCHOOLS_LABEL,
  EDUCATION_LEVEL_LABELS,
  SchoolOption,
  TARGET_SCHOOL_FIELD,
  filterSelectableSchools,
  formatSchoolAudienceSummary,
} from "../../../../utils/formacion/training-display";
import styles from "./styles.module.scss";

type SchoolTagRenderProps = Parameters<
  NonNullable<SelectProps<number[]>["tagRender"]>
>[0];

interface Props {
  form: FormInstance;
  schools: SchoolOption[];
  isEditing: boolean;
  /** Audiencia YA persistida (`training.school_ids`); [] cuando es "todos". */
  trainingSchoolIds: number[];
  /**
   * Quitar un colegio que ya tenía registrantes requiere confirmar antes de
   * aplicarse (design §5/§6). Resuelve `true` para aplicar la quita, `false`
   * para restaurar el chip.
   */
  onSchoolRemovalRequested: (schoolId: number) => Promise<boolean>;
  schoolSelectRef: React.RefObject<RefSelectProps | null>;
  subjectCatalog: SubjectCatalogItem[];
  subjectCatalogLoading: boolean;
}

const LEVELS: TrainingEducationLevel[] = ["KINDER", "PRIMARIA"];

const renderSchoolTag = ({ label, closable, onClose }: SchoolTagRenderProps) => (
  <Tag
    closable={closable}
    onClose={onClose}
    closeIcon={
      closable ? (
        <CloseOutlined
          aria-label={`Quitar ${String(label)}`}
          title={`Quitar ${String(label)}`}
        />
      ) : undefined
    }
    style={{ marginInlineEnd: 4 }}
  >
    {label}
  </Tag>
);

const AudienceTab: React.FC<Props> = ({
  form,
  schools,
  isEditing,
  trainingSchoolIds,
  onSchoolRemovalRequested,
  schoolSelectRef,
  subjectCatalog,
  subjectCatalogLoading,
}) => {
  const targetSchoolIds: number[] =
    Form.useWatch(TARGET_SCHOOL_FIELD, form) ?? [ALL_SCHOOLS];
  const audienceTeachers = Form.useWatch("audience_teachers", form) ?? false;
  const audienceCoordinators =
    Form.useWatch("audience_coordinators", form) ?? false;
  const educationLevel: TrainingEducationLevel | undefined = Form.useWatch(
    "education_level",
    form
  );
  const subjectIds: number[] = Form.useWatch("subjectIds", form) ?? [];

  const availableSubjects = useMemo(
    () =>
      educationLevel
        ? subjectCatalog.filter((subject) =>
            subject.levels.includes(educationLevel)
          )
        : [],
    [subjectCatalog, educationLevel]
  );

  const toggleUserType = (field: "audience_teachers" | "audience_coordinators") => {
    form.setFieldValue(field, !form.getFieldValue(field));
    form.validateFields(["audience_teachers"]).catch(() => undefined);
  };

  const selectLevel = (level: TrainingEducationLevel) => {
    if (form.getFieldValue("education_level") === level) return;
    form.setFieldValue("education_level", level);
    form.setFieldValue("subjectIds", []);
    form.validateFields(["education_level"]).catch(() => undefined);
  };

  const toggleSubject = (id: number) => {
    const current: number[] = form.getFieldValue("subjectIds") ?? [];
    const next = current.includes(id)
      ? current.filter((subjectId) => subjectId !== id)
      : [...current, id];
    form.setFieldValue("subjectIds", next);
    form.validateFields(["subjectIds"]).catch(() => undefined);
  };

  const summarySubjectNames = subjectCatalog
    .filter((subject) => subjectIds.includes(subject.id))
    .map((subject) => subject.name)
    .join(", ");

  const selectableSchools = useMemo(
    () => filterSelectableSchools(schools),
    [schools]
  );

  const schoolOptions = useMemo(
    () => [
      { value: ALL_SCHOOLS, label: ALL_SCHOOLS_LABEL },
      ...selectableSchools.map((school) => ({
        value: school.id,
        label: school.name,
      })),
    ],
    [selectableSchools]
  );

  // SCRUM-1098 design §6: ALL_SCHOOLS and an individual pick are mutually
  // exclusive in both directions, and removing a school that is part of the
  // training's already-persisted audience is gated behind a confirmation
  // (design §5) — never for a school added and removed within this same
  // edit session, and never for the bulk "Todos los colegios" collapse.
  const handleSchoolsChange = (nextValues: number[]) => {
    const previous: number[] = form.getFieldValue(TARGET_SCHOOL_FIELD) ?? [
      ALL_SCHOOLS,
    ];

    if (nextValues.includes(ALL_SCHOOLS) && !previous.includes(ALL_SCHOOLS)) {
      commitSchoolSelection([ALL_SCHOOLS]);
      return;
    }
    if (
      previous.includes(ALL_SCHOOLS) &&
      nextValues.some((id) => id !== ALL_SCHOOLS)
    ) {
      commitSchoolSelection(nextValues.filter((id) => id !== ALL_SCHOOLS));
      return;
    }

    const removedId = previous.find(
      (id) => id !== ALL_SCHOOLS && !nextValues.includes(id)
    );
    const resolved = nextValues.length === 0 ? [ALL_SCHOOLS] : nextValues;

    if (
      removedId !== undefined &&
      isEditing &&
      trainingSchoolIds.includes(removedId)
    ) {
      onSchoolRemovalRequested(removedId).then((approved) => {
        if (approved) commitSchoolSelection(resolved);
      });
      return;
    }

    commitSchoolSelection(resolved);
  };

  const commitSchoolSelection = (next: number[]) => {
    form.setFieldValue(TARGET_SCHOOL_FIELD, next);
    form.validateFields([TARGET_SCHOOL_FIELD]).catch(() => undefined);
  };

  const summarySchoolsText = formatSchoolAudienceSummary(
    targetSchoolIds,
    selectableSchools
  );

  const summaryUsers = audienceTeachers && audienceCoordinators
    ? "Profesores y coordinadores"
    : audienceCoordinators
    ? "Coordinadores"
    : audienceTeachers
    ? "Profesores"
    : "Sin seleccionar";

  return (
    <div>
      <Form.Item label="Colegios">
        <Select
          ref={schoolSelectRef}
          mode="multiple"
          size="large"
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          allowClear
          aria-label="Colegios destino"
          value={targetSchoolIds}
          options={schoolOptions}
          tagRender={renderSchoolTag}
          onChange={handleSchoolsChange}
        />
        <Form.Item name={TARGET_SCHOOL_FIELD} noStyle initialValue={[ALL_SCHOOLS]}>
          <input type="hidden" />
        </Form.Item>
      </Form.Item>
      <div className={styles.readonlyHint}>
        {targetSchoolIds.includes(ALL_SCHOOLS)
          ? "La capacitación queda visible para todos los colegios."
          : isEditing
          ? "La capacitación queda visible solo para los colegios seleccionados."
          : "La capacitación se crea solo para los colegios seleccionados."}
      </div>

      <Form.Item label="Tipo de usuario" required>
        <div className={styles.pillGroup}>
          <button
            type="button"
            className={`${styles.pill} ${
              audienceTeachers ? styles.pillActive : ""
            }`}
            onClick={() => toggleUserType("audience_teachers")}
          >
            Profesores
          </button>
          <button
            type="button"
            className={`${styles.pill} ${
              audienceCoordinators ? styles.pillActive : ""
            }`}
            onClick={() => toggleUserType("audience_coordinators")}
          >
            Coordinadores
          </button>
        </div>
        <Form.Item shouldUpdate noStyle>
          {() => {
            const [error] = form.getFieldError("audience_teachers");
            return error ? (
              <div className={styles.errorText}>{error}</div>
            ) : null;
          }}
        </Form.Item>
        <Form.Item name="audience_coordinators" noStyle initialValue={false}>
          <input type="hidden" />
        </Form.Item>
        <Form.Item
          name="audience_teachers"
          noStyle
          initialValue={false}
          rules={[
            {
              validator: () =>
                form.getFieldValue("audience_teachers") ||
                form.getFieldValue("audience_coordinators")
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Debes seleccionar al menos una")
                    ),
            },
          ]}
        >
          <input type="hidden" />
        </Form.Item>
      </Form.Item>

      <Form.Item label="Sección" required>
        <div className={styles.pillGroup}>
          {LEVELS.map((level) => (
            <button
              key={level}
              type="button"
              className={`${styles.pill} ${
                educationLevel === level ? styles.pillActive : ""
              }`}
              onClick={() => selectLevel(level)}
            >
              {EDUCATION_LEVEL_LABELS[level]}
            </button>
          ))}
        </div>
        <Form.Item
          name="education_level"
          noStyle
          rules={[
            { required: true, message: "Debes seleccionar una sección" },
          ]}
        >
          <input type="hidden" />
        </Form.Item>
      </Form.Item>

      <Form.Item label="Asignaturas" required>
        {subjectCatalogLoading ? (
          <div className={styles.emptyState}>Cargando asignaturas...</div>
        ) : !educationLevel ? (
          <div className={styles.emptyState}>
            Seleccioná una sección para ver las asignaturas disponibles.
          </div>
        ) : availableSubjects.length === 0 ? (
          <div className={styles.emptyState}>
            No hay asignaturas disponibles para esta sección.
          </div>
        ) : (
          <div className={styles.pillGroup}>
            {availableSubjects.map((subject) => (
              <button
                key={subject.id}
                type="button"
                className={`${styles.pill} ${
                  subjectIds.includes(subject.id) ? styles.pillActive : ""
                }`}
                onClick={() => toggleSubject(subject.id)}
              >
                {subject.name}
              </button>
            ))}
          </div>
        )}
        <Form.Item shouldUpdate noStyle>
          {() => {
            const [error] = form.getFieldError("subjectIds");
            return error ? (
              <div className={styles.errorText}>{error}</div>
            ) : null;
          }}
        </Form.Item>
        <Form.Item
          name="subjectIds"
          noStyle
          initialValue={[]}
          rules={[
            {
              validator: () =>
                (form.getFieldValue("subjectIds") ?? []).length > 0
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Debes seleccionar al menos una")
                    ),
            },
          ]}
        >
          <input type="hidden" />
        </Form.Item>
      </Form.Item>

      <div className={styles.summaryCard}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Colegios</span>
          <span>{summarySchoolsText}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Usuarios</span>
          <span>{summaryUsers}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Sección</span>
          <span>
            {educationLevel
              ? EDUCATION_LEVEL_LABELS[educationLevel]
              : "Sin seleccionar"}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Asignaturas</span>
          <span>{summarySubjectNames || "Sin seleccionar"}</span>
        </div>
      </div>
    </div>
  );
};

export default AudienceTab;
